/**
 * Law OS v2 — Litigation Workspace (Phase 4)
 * Single-screen tabbed layout with sticky header + AI assistant slide-over.
 * Tab content is split into modular components under /components/case/CaseTabs.jsx.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, Briefcase, Clock, Gavel, FileText, Receipt, Users, MessageSquare,
    CheckSquare, StickyNote, Activity, Sparkles, Pencil, History, Repeat,
    FileSignature, Scale, Download, UserCheck, RefreshCw, CheckCircle2, Loader2, Flame, UserPlus, Cpu
} from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, GhostButton } from "@/components/Ui";
import HistoryDrawer from "@/components/HistoryDrawer";
import { fmtDateTime } from "@/lib/time";
import {
    CasePill, SectionCard, OverviewTab, TimelineTab, HearingsTab, TasksTab,
    NotesTab, DocumentsTab, CommunicationsTab, BillingTab, TeamTab, PartiesTab, AiAssistantPanel,
    CourtHistoryTab
} from "@/components/case/CaseTabs";

const TABS = [
    { id: "overview",       label: "Overview",       icon: Briefcase },
    { id: "parties",        label: "Parties",        icon: UserCheck },
    { id: "court-history",  label: "Court History",  icon: History },
    { id: "timeline",       label: "Timeline",       icon: Clock },
    { id: "hearings",       label: "Hearings",       icon: Gavel },
    { id: "tasks",          label: "Tasks",          icon: CheckSquare },
    { id: "notes",          label: "Notes",          icon: StickyNote },
    { id: "documents",      label: "Documents",      icon: FileText },
    { id: "communications", label: "Comms",          icon: MessageSquare },
    { id: "billing",        label: "Billing",        icon: Receipt },
    { id: "team",           label: "Team",           icon: Users },
    { id: "orders",         label: "Orders",         icon: Scale },
];

export default function CaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    // Allow deep-linking to a specific tab via ?tab=documents (used by
    // the Legal Search → Attach toast so the user lands directly on
    // their newly-attached headnote).
    const initialTab = (() => {
        try {
            const p = new URLSearchParams(window.location.search).get("tab");
            return TABS.some(t => t.id === p) ? p : "overview";
        } catch { return "overview"; }
    })();
    const [caseDoc, setCaseDoc] = useState(null);
    const [client, setClient] = useState(null);
    const [clientsList, setClientsList] = useState([]);
    const [showNewClient, setShowNewClient] = useState(false);
    const [hearings, setHearings] = useState([]);
    const [docs, setDocs] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [staff, setStaff] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [casePrefixes, setCasePrefixes] = useState([]);
    const [tab, setTab] = useState(initialTab);
    const [reassign, setReassign] = useState(null);
    const [edit, setEdit] = useState(null);
    const [editSaving, setEditSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshDiff, setRefreshDiff] = useState(null);
    const [refreshQueued, setRefreshQueued] = useState(false);
    const autoSyncChecked = useRef(false);
    const pollRef = useRef(null);

    // Derive queued state from caseDoc on every load
    const isRefreshQueued = refreshQueued
        || caseDoc?.ecourts_refresh_status === "queued";

    // Cleanup poll on unmount
    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const loadAll = useCallback(async () => {
        try {
            const { data: c } = await api.get(`/cases/${id}`);
            setCaseDoc(c);
            const [cli, h, d, inv, st, ts] = await Promise.all([
                c.client_id ? api.get(`/clients/${c.client_id}`).then(r => r.data).catch(() => null) : null,
                api.get(`/hearings`, { params: { case_id: id } }).then(r => r.data).catch(() => []),
                api.get(`/documents`, { params: { case_id: id } }).then(r => r.data).catch(() => []),
                api.get(`/invoices`, { params: { case_id: id } }).then(r => r.data).catch(() => []),
                api.get(`/users`).then(r => r.data.filter(u => u.role !== "client_portal" && u.is_active !== false)).catch(() => []),
                api.get(`/tasks`, { params: { case_id: id } }).then(r => r.data).catch(() => []),
            ]);
            setClient(cli);
            setHearings(h);
            setDocs(d);
            setInvoices(inv);
            setStaff(st);
            setTasks(ts);
        } catch (e) {
            toast.error(apiError(e));
        }
    }, [id]);

    useEffect(() => {
        const t = setTimeout(loadAll, 0);
        return () => clearTimeout(t);
    }, [loadAll]);

    // ── Poll /refresh-status every 30s while refresh is queued ───────────────
    // Defined AFTER loadAll so it can safely reference it in the closure.
    const startPolling = useCallback((cid) => {
        if (pollRef.current) return; // already polling
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/ecourts/cases/${cid}/refresh-status`);
                if (data.status === "done" || data.status === "failed") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setRefreshQueued(false);
                    await loadAll();                         // reload full case
                    if (data.status === "done") {
                        setRefreshDiff({ queued: true, changes: data.changes || {} });
                        if (Object.keys(data.changes || {}).length > 0)
                            toast.success("Case updated from eCourts");
                        else
                            toast("eCourts refresh complete — no changes");
                    } else {
                        toast.error("eCourts refresh failed — please try again");
                    }
                }
            } catch (_) { /* silent poll failure */ }
        }, 30_000);
    }, [loadAll]);

    // Resume polling if page is reloaded while a refresh is still in progress
    useEffect(() => {
        if (caseDoc?.ecourts_refresh_status === "queued") {
            setRefreshQueued(true);
            startPolling(caseDoc._id || id);
            const timeout = setTimeout(() => {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setRefreshQueued(false);
                toast("Refresh timed out — please try again");
            }, 20 * 60 * 1000);
            return () => { clearTimeout(timeout); };
        }
    }, [caseDoc?.ecourts_refresh_status]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-sync on open — only if admin has enabled it and case has a CNR.
    // Case-open guard: only auto-refresh if data is stale (> stale_hours old).
    // Manual "Refresh and Update" always works regardless of this guard.
    useEffect(() => {
        if (!caseDoc || autoSyncChecked.current) return;
        if (!caseDoc.cnr) return;
        autoSyncChecked.current = true;
        api.get("/ecourts/config")
            .then(({ data }) => {
                if (!data.auto_sync_on_open) return;
                const staleHours = data.auto_refresh_stale_hours ?? 15;
                const lastUpdated = caseDoc.ecourts_last_synced_at
                                 || caseDoc.ecourts_last_refreshed_at;
                if (lastUpdated) {
                    const hoursSince = (Date.now() - new Date(lastUpdated)) / (1000 * 60 * 60);
                    if (hoursSince < staleHours) return; // fresh enough — skip API call
                }
                refreshAndUpdate();
            })
            .catch(() => {});
    }, [caseDoc]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!caseDoc) return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"/>
            <span className="text-sm">Loading litigation…</span>
        </div>
    );

    const isLO = caseDoc.record_type === "legal_opinion";
    const assignedName = staff.find(s => s.id === caseDoc.assigned_to)?.name || "—";

    const doReassign = async () => {
        if (!reassign?.assignee_id || !reassign?.reason || reassign.reason.length < 3) {
            toast.error("Pick an assignee and write a reason (min 3 chars)"); return;
        }
        try {
            await api.post(`/cases/${id}/reassign`,
                { new_assignee_id: reassign.assignee_id, reason: reassign.reason },
                { headers: { "X-Change-Reason": reassign.reason } });
            toast.success("Litigation reassigned");
            setReassign(null);
            loadAll();
        } catch (e) { toast.error(apiError(e)); }
    };

    const appearNow = async () => {
        try {
            const { data } = await api.post(`/cases/${id}/appear-now`);
            toast.success(`Opening VC for ${data.court}…`);
            window.open(data.url, "_blank", "noopener,noreferrer");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not open VC";
            toast.error(msg);
        }
    };

    const openEdit = () => {
        if (casePrefixes.length === 0) {
            api.get("/case-type-prefixes")
                .then(r => setCasePrefixes(r.data?.prefixes || []))
                .catch(() => {});
        }
        // Load clients list for the picker (lazy, only once)
        if (clientsList.length === 0) {
            api.get("/clients").then(r => setClientsList(r.data || [])).catch(() => {});
        }
        setEdit({
            title: caseDoc.title || "",
            court_case_number: caseDoc.court_case_number || "",
            original_suit_number: caseDoc.original_suit_number || "",
            court: caseDoc.court || "",
            bench: caseDoc.bench || "",
            hearing_type: caseDoc.hearing_type || "",
            category: caseDoc.category || "",
            type: caseDoc.type || "",
            stage: caseDoc.stage || "",
            priority: caseDoc.priority || "medium",
            status: caseDoc.status || "open",
            description: caseDoc.description || "",
            client_id: caseDoc.client_id || "",
            reason: "",
        });
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!edit.reason || edit.reason.trim().length < 3) {
            toast.error("Remarks are mandatory (minimum 3 characters)");
            return;
        }
        const { reason, ...payload } = edit;
        const diff = {};
        Object.keys(payload).forEach(k => {
            if ((payload[k] ?? "") !== (caseDoc[k] ?? "")) diff[k] = payload[k];
        });
        if (Object.keys(diff).length === 0) {
            toast.error("No fields changed");
            return;
        }
        setEditSaving(true);
        try {
            await api.patch(`/cases/${id}`, diff, { headers: { "X-Change-Reason": reason.trim() } });
            toast.success("Litigation updated · logged to audit trail");
            setEdit(null);
            loadAll();
        } catch (e) { toast.error(apiError(e)); } finally { setEditSaving(false); }
    };

    const refreshAndUpdate = async () => {
        if (refreshing || isRefreshQueued) return;
        if (!caseDoc?.cnr) { toast.error("No CNR linked — cannot refresh from eCourts"); return; }
        setRefreshing(true);
        try {
            const { data } = await api.post(`/ecourts/cases/${id}/refresh-now`, {}, { timeout: 30_000 });
            if (data.done) {
                // Synchronous result — reload case and show diff immediately
                await loadAll();
                if (Object.keys(data.changes || {}).length > 0) {
                    setRefreshDiff({ queued: false, changes: data.changes || {} });
                    toast.success("Case updated from eCourts");
                } else {
                    toast("eCourts refresh complete — no changes detected");
                }
            } else if (data.queued) {
                // Fallback queued path (e.g. already-queued guard)
                setRefreshQueued(true);
                toast.info("eCourts refresh queued — you'll be notified when done");
                startPolling(id);
                setTimeout(() => {
                    if (pollRef.current) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        setRefreshQueued(false);
                        toast("Refresh timed out — please try again");
                    }
                }, 20 * 60 * 1000);
            }
        } catch (e) {
            toast.error(apiError(e) || "Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 overflow-x-hidden" data-testid="case-workspace">
            {/* ─────────── Workspace header (scrolls away with page) ─────────── */}
            <div className="bg-card border-b border-border" data-testid="case-header">
                <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-3 max-w-[1600px] mx-auto">
                    <Link to="/app/cases" data-testid="case-back-link"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-3">
                        <ArrowLeft className="w-3 h-3"/> Back to {isLO ? "Legal opinions" : "Litigations"}
                    </Link>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1">
                                {isLO ? "Legal Opinion" : "Litigation"} · <span className="font-mono">{caseDoc.internal_number}</span>
                            </div>
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground break-words" data-testid="case-detail-title">
                                {caseDoc.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                                {caseDoc.original_suit_number && (
                                    <span className="font-mono text-primary font-medium" data-testid="case-detail-suit-no">
                                        {caseDoc.original_suit_number}
                                    </span>
                                )}
                                {caseDoc.court_case_number && <span className="font-mono">· {caseDoc.court_case_number}</span>}
                                {caseDoc.court && <span>· {caseDoc.court}</span>}
                                {caseDoc.bench && <span>· {caseDoc.bench}</span>}
                                {caseDoc.stage && <CasePill label={caseDoc.stage}/>}
                                {caseDoc.status && <CasePill label={caseDoc.status}/>}
                                {caseDoc.ecourts_last_refreshed_at && (
                                    <span className="inline-flex items-center gap-1 bg-primary/10 border border-primary/20 px-2 py-0.5 rounded text-[11px] text-primary font-medium" data-testid="case-last-checked">
                                        <CheckCircle2 className="w-3 h-3"/>
                                        eCourts: {(() => {
                                            const ts = caseDoc.ecourts_last_synced_at || caseDoc.ecourts_last_refreshed_at;
                                            try {
                                                return new Date(ts).toLocaleString("en-IN", {
                                                    timeZone: "Asia/Kolkata",
                                                    day: "2-digit", month: "short", year: "numeric",
                                                    hour: "2-digit", minute: "2-digit", hour12: true,
                                                });
                                            } catch { return ts?.slice(0, 16).replace("T", " "); }
                                        })()}
                                    </span>
                                )}
                                {caseDoc.external_synced_at && (
                                    <span className="inline-flex items-center gap-1 bg-violet-500/10 border border-violet-500/20 px-2 py-0.5 rounded text-[11px] text-violet-600 dark:text-violet-400 font-medium" data-testid="case-scraper-synced-at">
                                        <Cpu className="w-3 h-3"/>
                                        Auto-synced: {(() => {
                                            try {
                                                return new Date(caseDoc.external_synced_at).toLocaleString("en-IN", {
                                                    timeZone: "Asia/Kolkata",
                                                    day: "2-digit", month: "short", year: "numeric",
                                                    hour: "2-digit", minute: "2-digit", hour12: true,
                                                });
                                            } catch { return caseDoc.external_synced_at?.slice(0, 16).replace("T", " "); }
                                        })()}
                                    </span>
                                )}
                                {/* HOT/COLD tier badge + toggle */}
                                <WatchTierBadge caseId={id} tier={caseDoc.watch_tier} onChanged={loadAll}/>
                            </div>
                        </div>

                        {/* Possibly-closed banner */}
                        {caseDoc.possibly_closed && (
                            <PossiblyClosedBanner caseId={id} onResolved={loadAll}/>
                        )}
                        <ActionToolbar
                            onAi={() => setAiOpen(true)}
                            onEdit={openEdit}
                            onHistory={() => setShowHistory(true)}
                            onReassign={() => setReassign({ assignee_id: caseDoc.assigned_to || "", reason: "" })}
                            onChat={async () => {
                                try {
                                    const { data } = await api.post("/chat/threads", { case_id: id });
                                    navigate(`/app/chat?thread=${data.id}`);
                                } catch (err) { toast.error(apiError(err)); }
                            }}
                            onAppear={appearNow}
                            onRefresh={refreshAndUpdate}
                            refreshing={refreshing}
                            isRefreshQueued={isRefreshQueued}
                            hasCnr={!!caseDoc?.cnr}
                            caseId={id}
                        />
                    </div>
                </div>
            </div>

            {/* ─────────── Sticky tab strip only ─────────── */}
            <div className="sticky top-16 z-20 bg-card border-b border-border" data-testid="case-sticky-header">
                <div className="px-4 sm:px-6 lg:px-8 max-w-[1600px] mx-auto">
                    <div className="flex gap-1 py-2 overflow-x-auto -mx-1 px-1" data-testid="case-detail-tabs">
                        {TABS.map(t => (
                            <button key={t.id} onClick={() => {
                                setTab(t.id);
                                // Reload parent data when switching to prop-fed tabs
                                // (hearings, docs, billing, overview) so they show fresh data
                                if (["hearings", "documents", "billing", "overview"].includes(t.id)) {
                                    loadAll();
                                }
                            }}
                                data-testid={`case-tab-${t.id}`}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${
                                    tab === t.id
                                        ? "bg-primary/10 text-primary"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                }`}>
                                <t.icon className="w-3.5 h-3.5" strokeWidth={1.75}/>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ─────────── Tab content ─────────── */}
            <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1600px] mx-auto">
                {tab === "overview" && (
                    <OverviewTab caseDoc={caseDoc} client={client} assignedName={assignedName}
                        hearings={hearings} tasks={tasks} invoices={invoices}/>
                )}
                {tab === "parties" && <PartiesTab caseDoc={caseDoc} client={client} onSaved={loadAll}/>}
                {tab === "court-history" && <CourtHistoryTab caseId={id} cnr={caseDoc.cnr} onSynced={loadAll}/>}
                {tab === "timeline" && <TimelineTab caseId={id}/>}
                {tab === "hearings" && <HearingsTab hearings={hearings}/>}
                {tab === "tasks" && <TasksTab caseId={id}/>}
                {tab === "notes" && <NotesTab caseDoc={caseDoc} onSaved={loadAll}/>}
                {tab === "documents" && <DocumentsTab docs={docs} caseId={id}/>}
                {tab === "communications" && <CommunicationsTab caseId={id}/>}
                {tab === "billing" && <BillingTab invoices={invoices} caseId={id}/>}
                {tab === "team" && (
                    <TeamTab caseDoc={caseDoc} staff={staff}
                        onReassign={() => setReassign({ assignee_id: caseDoc.assigned_to || "", reason: "" })}/>
                )}
                {tab === "orders" && <OrdersTab caseId={id} cnr={caseDoc.cnr}/>}
            </div>

            {/* ─────────── AI Assistant slide-over panel ─────────── */}
            <AiAssistantPanel open={aiOpen} onClose={() => setAiOpen(false)} caseDoc={caseDoc} caseId={id}/>

            {/* ─────────── Refresh & Update diff popup ─────────── */}
            {refreshDiff && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                     onClick={() => setRefreshDiff(null)}>
                    <div className="bg-card border border-border rounded-2xl shadow-lawos-lg w-full max-w-lg"
                         onClick={e => e.stopPropagation()} data-testid="refresh-diff-modal">
                        <div className="px-6 pt-5 pb-4 border-b border-border flex items-center gap-3">
                            <div className={`p-2 rounded-full ${Object.keys(refreshDiff.changes || {}).length > 0 ? "bg-green-500/10" : "bg-muted"}`}>
                                <RefreshCw className={`w-4 h-4 ${Object.keys(refreshDiff.changes || {}).length > 0 ? "text-green-500" : "text-muted-foreground"}`}/>
                            </div>
                            <div>
                                <h2 className="text-sm font-semibold text-foreground">
                                    {Object.keys(refreshDiff.changes || {}).length > 0 ? "Updates Found" : "Already Up to Date"}
                                </h2>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {Object.keys(refreshDiff.changes || {}).length > 0
                                        ? `${Object.keys(refreshDiff.changes).length} field(s) changed`
                                        : "eCourts confirmed no changes since last refresh"}
                                </p>
                            </div>
                            <button onClick={() => setRefreshDiff(null)}
                                className="ml-auto text-muted-foreground hover:text-foreground text-lg leading-none">&times;</button>
                        </div>
                        <div className="px-6 py-4">
                            {Object.keys(refreshDiff.changes || {}).length > 0 ? (
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-muted-foreground">
                                            <th className="text-left pb-2 font-medium w-1/3">Field</th>
                                            <th className="text-left pb-2 font-medium">Before</th>
                                            <th className="text-left pb-2 font-medium">After</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {Object.entries(refreshDiff.changes).map(([label, diff]) => (
                                            <tr key={label}>
                                                <td className="py-2 font-medium text-foreground capitalize">
                                                    {label.replace(/_/g, " ")}
                                                </td>
                                                <td className="py-2 text-muted-foreground line-through">{diff.prev}</td>
                                                <td className="py-2 text-green-500 font-medium">{diff.new}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-sm text-muted-foreground py-2">
                                    All fields match the latest eCourts data.
                                </div>
                            )}
                        </div>
                        <div className="px-6 pb-5">
                            <button onClick={() => setRefreshDiff(null)} data-testid="refresh-diff-close-btn"
                                className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity">
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating AI FAB (raised above the ChatWidget) */}
            {!aiOpen && (
                <button onClick={() => setAiOpen(true)} data-testid="case-ai-fab"
                    className="fixed bottom-24 right-6 z-30 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-primary text-primary-foreground shadow-lawos-lg hover:shadow-lawos-md hover:scale-105 transition-all">
                    <Sparkles className="w-4 h-4"/>
                    <span className="text-sm font-medium hidden sm:inline">Ask AI</span>
                </button>
            )}

            {/* Edit modal */}
            <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit litigation" size="lg" testId="case-edit-modal">
                {edit && (
                    <form onSubmit={saveEdit} className="space-y-4">
                        <Field label="Title">
                            <Input required value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })} data-testid="case-edit-title"/>
                        </Field>

                        {/* Client picker — select existing or create new */}
                        <Field label="Client">
                            <div className="flex gap-2" data-testid="case-edit-client-row">
                                <select
                                    value={edit.client_id}
                                    onChange={e => setEdit({ ...edit, client_id: e.target.value })}
                                    data-testid="case-edit-client-select"
                                    className="flex-1 bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-lg">
                                    <option value="">— No client —</option>
                                    {clientsList.map(c => (
                                        <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ""}{c.name}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setShowNewClient(true)}
                                    data-testid="case-edit-new-client-btn"
                                    title="Create a new client"
                                    className="flex-none inline-flex items-center gap-1.5 px-3 py-2 border border-border text-xs text-muted-foreground hover:border-primary hover:text-primary rounded-lg whitespace-nowrap">
                                    <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                    New
                                </button>
                            </div>
                            {edit.client_id && (
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    {clientsList.find(c => c.id === edit.client_id)?.name || ""}
                                </div>
                            )}
                        </Field>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Court case #"><Input value={edit.court_case_number} onChange={e => setEdit({ ...edit, court_case_number: e.target.value })} data-testid="case-edit-court-number"/></Field>
                            <Field label="Original Case Number">
                                <input
                                    list="case-type-prefixes-datalist"
                                    value={edit.original_suit_number}
                                    onChange={e => setEdit({ ...edit, original_suit_number: e.target.value })}
                                    placeholder="e.g. OS 253/2014"
                                    data-testid="case-edit-suit-number"
                                    className="w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm text-foreground rounded-lg"
                                />
                                <datalist id="case-type-prefixes-datalist">
                                    {casePrefixes.map(p => (
                                        <option key={p.code} value={`${p.code} `}>{p.label}</option>
                                    ))}
                                </datalist>
                                {casePrefixes.length > 0 && (
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        Tip · type a prefix (e.g. <span className="text-primary font-mono">OS</span>, <span className="text-primary font-mono">WP(C)</span>) to autocomplete from {casePrefixes.length} configured codes.
                                    </div>
                                )}
                            </Field>
                            <Field label="Court"><Input value={edit.court} onChange={e => setEdit({ ...edit, court: e.target.value })} data-testid="case-edit-court"/></Field>
                            <Field label="Bench"><Input value={edit.bench} onChange={e => setEdit({ ...edit, bench: e.target.value })}/></Field>
                            <Field label="Hearing type"><Input value={edit.hearing_type} onChange={e => setEdit({ ...edit, hearing_type: e.target.value })}/></Field>
                            <Field label="Category"><Input value={edit.category} onChange={e => setEdit({ ...edit, category: e.target.value })}/></Field>
                            <Field label="Type"><Input value={edit.type} onChange={e => setEdit({ ...edit, type: e.target.value })}/></Field>
                            <Field label="Stage"><Input value={edit.stage} onChange={e => setEdit({ ...edit, stage: e.target.value })} data-testid="case-edit-stage"/></Field>
                            <Field label="Priority">
                                <Select value={edit.priority} onChange={e => setEdit({ ...edit, priority: e.target.value })}
                                    options={["low", "medium", "high", "urgent"]} data-testid="case-edit-priority"/>
                            </Field>
                            <Field label="Status">
                                <Select value={edit.status} onChange={e => setEdit({ ...edit, status: e.target.value })}
                                    options={["open", "on_hold", "closed", "won", "lost", "settled"]}/>
                            </Field>
                        </div>
                        <Field label="Description"><Textarea rows="3" value={edit.description} onChange={e => setEdit({ ...edit, description: e.target.value })}/></Field>
                        <Field label="Remarks (mandatory · logged to audit trail)" hint="Explain why this edit is being made. Minimum 3 characters.">
                            <Textarea rows="2" value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} data-testid="case-edit-reason" placeholder="e.g. Court case number corrected after registry confirmation"/>
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEdit(null)} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
                            <GoldButton type="submit" disabled={editSaving} data-testid="case-edit-save-btn">{editSaving ? "Saving…" : "Save changes"}</GoldButton>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Reassign modal */}
            <Modal open={!!reassign} onClose={() => setReassign(null)} title="Reassign case" testId="reassign-modal">
                {reassign && (
                    <div className="space-y-4">
                        <Field label="New assignee">
                            <Select value={reassign.assignee_id} onChange={e => setReassign({...reassign, assignee_id: e.target.value})} data-testid="reassign-assignee-select"
                                options={[{value: "", label: "— select —"}, ...staff.map(s => ({value: s.id, label: `${s.name} · ${s.role}`}))]}/>
                        </Field>
                        <Field label="Reason (required, ≥3 chars, logged to audit trail)">
                            <Textarea required rows="3" value={reassign.reason} onChange={e => setReassign({...reassign, reason: e.target.value})} data-testid="reassign-reason-input" placeholder="e.g. Original assignee on leave; routing to next available advocate."/>
                        </Field>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setReassign(null)} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
                            <GoldButton onClick={doReassign} data-testid="reassign-confirm-btn">Reassign</GoldButton>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Create new client inline from edit form */}
            {showNewClient && (
                <NewClientMiniModal
                    onClose={() => setShowNewClient(false)}
                    onCreated={(newClient) => {
                        setClientsList(prev => [...prev, newClient]);
                        setEdit(prev => ({ ...prev, client_id: newClient.id }));
                        setShowNewClient(false);
                        toast.success(`Client "${newClient.name}" created and linked`);
                    }}
                />
            )}

            <HistoryDrawer open={showHistory} onClose={() => setShowHistory(false)} entity="case" entityId={id} title={`${caseDoc.internal_number} · ${caseDoc.title}`}/>
        </div>
    );
}

// ─────────── Action toolbar ───────────────────────────────────────────────────

function ActionToolbar({ onAi, onEdit, onHistory, onReassign, onChat, onAppear, onRefresh, refreshing, hasCnr, isRefreshQueued, caseId }) {
    const [moreOpen, setMoreOpen] = React.useState(false);
    const moreRef = React.useRef(null);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="flex items-center gap-2 mt-3 sm:mt-0 w-full sm:w-auto flex-shrink-0" data-testid="case-action-toolbar">
            {/* ── Primary: Refresh & Update — always visible ── */}
            <button
                onClick={onRefresh}
                disabled={!hasCnr || refreshing || isRefreshQueued}
                title={isRefreshQueued ? "eCourts refresh in progress — please wait (~10 min)" : !hasCnr ? "No CNR linked" : "Refresh case from eCourts"}
                data-testid="case-refresh-btn"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border
                    ${isRefreshQueued
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-500 cursor-not-allowed"
                        : refreshing
                            ? "border-primary/50 bg-primary/10 text-primary cursor-not-allowed"
                            : hasCnr
                                ? "border-primary bg-primary/5 text-primary hover:bg-primary/15 active:scale-95"
                                : "border-border bg-muted text-muted-foreground cursor-not-allowed opacity-50"}`}>
                <RefreshCw className={`w-3.5 h-3.5 flex-shrink-0 ${(refreshing || isRefreshQueued) ? "animate-spin" : ""}`}/>
                <span className="hidden sm:inline">
                    {refreshing ? "Queuing…" : isRefreshQueued ? "Refreshing…" : "Refresh & Update"}
                </span>
            </button>

            {/* ── Primary: Edit ── */}
            <button onClick={onEdit} data-testid="case-edit-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:border-primary/50 hover:bg-primary/5 transition-colors">
                <Pencil className="w-3.5 h-3.5"/>
                <span className="hidden sm:inline">Edit</span>
            </button>

            {/* ── Primary: Ask AI ── */}
            <button onClick={onAi} data-testid="case-ai-summary-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity shadow-sm">
                <Sparkles className="w-3.5 h-3.5"/>
                <span className="hidden sm:inline">Ask AI</span>
            </button>

            {/* ── Secondary: overflow "⋯" menu ── */}
            <div className="relative" ref={moreRef}>
                <button onClick={() => setMoreOpen(v => !v)} data-testid="case-more-actions-btn"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors text-base font-bold">
                    ⋯
                </button>
                {moreOpen && (
                    <div className="absolute right-0 top-10 z-30 w-44 bg-card border border-border rounded-xl shadow-lawos-lg py-1 overflow-hidden"
                         data-testid="case-more-menu">
                        {[
                            { label: "Appear Now",  icon: Sparkles,      action: onAppear },
                            { label: "Chat",        icon: MessageSquare, action: onChat },
                            { label: "History",     icon: History,       action: onHistory },
                            { label: "Reassign",    icon: Repeat,        action: onReassign },
                            { label: "AI Draft",    icon: FileSignature, action: () => window.location.href = `/app/ai-docs?case_id=${caseId}` },
                        ].map(({ label, icon: Icon, action }) => (
                            <button key={label} onClick={() => { action?.(); setMoreOpen(false); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors">
                                <Icon className="w-3.5 h-3.5 text-muted-foreground"/>
                                {label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ToolButton({ children, icon: Icon, testId, ...rest }) {
    return (
        <button {...rest} data-testid={testId}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 text-xs font-medium text-foreground transition-colors">
            <Icon className="w-3.5 h-3.5"/> <span className="hidden sm:inline">{children}</span>
        </button>
    );
}

// ─────────── eCourts Orders & Judgments tab (preserved from legacy) ───────────

function OrdersTab({ caseId, cnr }) {
    const [orders, setOrders] = useState([]);
    const [busy, setBusy] = useState(false);
    const [aiBusy, setAiBusy] = useState({});
    const [expanded, setExpanded] = useState({});
    const [loaded, setLoaded] = useState(false);
    const [perms, setPerms] = useState({ download: true, md: true, ai: true });

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/ecourts/case/${caseId}/orders`);
            setOrders(data.orders || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoaded(true); }
    }, [caseId]);

    const loadPerms = useCallback(async () => {
        try {
            const { data } = await api.get(`/ecourts/config`);
            const eps = data.endpoint_prefs || {};
            const isOn = (k) => {
                const p = eps[k] || {};
                if (p.platform_force_off) return false;
                if (p.platform_force_on) return true;
                if (p.platform_enabled === false) return false;
                return p.enabled !== false;
            };
            setPerms({
                download: isOn("order_download"),
                md: isOn("order_md"),
                ai: isOn("order_ai"),
            });
        } catch { /* optimistic */ }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => { load(); loadPerms(); }, 0);
        return () => clearTimeout(t);
    }, [load, loadPerms]);

    const syncOrders = async () => {
        if (!cnr) { toast.error("This case has no CNR linked — sync CNR first"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/ecourts/case/${caseId}/sync-orders`);
            setOrders(data.orders || []);
            toast.success(`Synced · ${data.inserted} new · ${data.updated} updated · ${data.total} total`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const runAi = async (filename, force = false) => {
        setAiBusy(p => ({ ...p, [filename]: true }));
        try {
            const url = `/ecourts/case/${caseId}/orders/${encodeURIComponent(filename)}/ai-summary` + (force ? "?force=true" : "");
            const { data } = await api.post(url);
            setOrders(curr => curr.map(o => o.filename === filename
                ? { ...o, ai_summary: data.ai_summary, ai_generated_at: data.ai_generated_at } : o));
            setExpanded(p => ({ ...p, [filename]: true }));
            toast.success(data.cached ? "Loaded cached summary" : (force ? "AI summary refreshed" : "AI summary ready"));
        } catch (e) { toast.error(apiError(e)); }
        finally { setAiBusy(p => ({ ...p, [filename]: false })); }
    };

    const downloadOrder = async (filename, mode = "pdf") => {
        // PDFs can't be opened via window.open(url) because a new tab can't
        // send the Authorization header — backend rejects with 401. Fetch as
        // an authenticated blob, then open the local object-URL instead.
        // `mode` = "pdf" | "md"
        const path = mode === "md" ? "download-md" : "download";
        try {
            const r = await api.get(
                `/ecourts/case/${caseId}/orders/${encodeURIComponent(filename)}/${path}`,
                { responseType: "blob" }
            );
            const mime = mode === "md" ? "text/markdown" : "application/pdf";
            const blob = new Blob([r.data], { type: mime });
            const objectUrl = URL.createObjectURL(blob);
            const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
            if (!win) {
                // Popup blocked → fall back to triggering a download
                const a = document.createElement("a");
                a.href = objectUrl; a.download = filename;
                document.body.appendChild(a); a.click(); a.remove();
            }
            // Revoke after a delay so the new tab has time to read it
            setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        } catch (e) {
            // Distinguish "vendor doesn't have this file" (ORDER_NOT_FOUND / 404)
            // from a genuine platform error — the former is a data availability
            // issue at eCourts (nothing broken here), the latter is our bug.
            const detail = e?.response?.data?.detail || "";
            const status = e?.response?.status;
            if (status === 404 || /ORDER_NOT_FOUND/i.test(detail)) {
                const label = mode === "md" ? "markdown copy" : "PDF";
                toast.error(`eCourts has no ${label} on file for this order. The court may not have published it yet — try Sync orders, or check back later.`);
            } else {
                toast.error(apiError(e));
            }
        }
    };

    return (
        <SectionCard title="eCourts orders & judgments" icon={Scale} testId="case-orders-tab"
            action={
                <button onClick={syncOrders} disabled={busy || !cnr} data-testid="sync-orders-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 disabled:opacity-40">
                    <Download className="w-3.5 h-3.5"/> {busy ? "Syncing…" : "Sync orders"}
                </button>
            }>
            <div className="text-xs text-muted-foreground mb-3">
                {cnr ? <>CNR: <span className="font-mono text-foreground">{cnr}</span></>
                     : <span className="text-amber-600">No CNR linked. Link one first to sync orders.</span>}
            </div>

            {loaded && orders.length === 0 && (
                <div className="border border-dashed border-border rounded-xl p-8 text-center text-sm text-muted-foreground" data-testid="orders-empty">
                    No orders synced yet. Click <b className="text-primary">Sync orders</b> to pull from eCourts.
                </div>
            )}

            <div className="space-y-3">
                {orders.map(o => (
                    <div key={o.id} className="border border-border rounded-xl p-4 bg-background/40" data-testid={`order-${o.id}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ring-1 ${
                                        o.kind === "judgment" ? "bg-primary/10 text-primary ring-primary/20" : "bg-muted text-muted-foreground ring-border"
                                    }`}>{o.kind || "order"}</span>
                                    <span className="text-sm text-foreground truncate">{o.description || o.filename}</span>
                                </div>
                                <div className="text-[10px] text-muted-foreground flex gap-3">
                                    {o.order_date && <span>Date: {o.order_date}</span>}
                                    <span className="font-mono truncate">{o.filename}</span>
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                {perms.download && (
                                    <GhostButton onClick={() => downloadOrder(o.filename, "pdf")} data-testid={`order-download-${o.id}`}>
                                        <Download className="w-3.5 h-3.5 inline mr-1.5"/> Download
                                    </GhostButton>
                                )}
                                {perms.md && (
                                    <GhostButton onClick={() => downloadOrder(o.filename, "md")} data-testid={`order-download-md-${o.id}`}>
                                        <FileText className="w-3.5 h-3.5 inline mr-1.5"/> Markdown
                                    </GhostButton>
                                )}
                                {perms.ai && (
                                    <GhostButton onClick={() => runAi(o.filename)} disabled={aiBusy[o.filename]} data-testid={`order-ai-${o.id}`}>
                                        <Sparkles className="w-3.5 h-3.5 inline mr-1.5"/>
                                        {aiBusy[o.filename] ? "Analysing…" : (o.ai_summary ? "View AI" : "AI summary")}
                                    </GhostButton>
                                )}
                            </div>
                        </div>
                        {o.ai_summary && expanded[o.filename] && (
                            <div className="mt-3 pt-3 border-t border-border text-xs" data-testid={`order-ai-body-${o.id}`}>
                                <AiSummaryBlock summary={o.ai_summary} generatedAt={o.ai_generated_at}
                                    onRefresh={() => runAi(o.filename, true)} refreshBusy={!!aiBusy[o.filename]}/>
                                <button onClick={() => setExpanded(p => ({ ...p, [o.filename]: false }))}
                                    className="mt-2 text-[10px] text-muted-foreground hover:text-primary uppercase tracking-[0.16em]">
                                    Collapse
                                </button>
                            </div>
                        )}
                        {o.ai_summary && !expanded[o.filename] && (
                            <button onClick={() => setExpanded(p => ({ ...p, [o.filename]: true }))}
                                className="mt-2 text-[10px] text-primary hover:opacity-80 uppercase tracking-[0.16em]">
                                Show AI summary ↓
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

// ─── GPT-4o-mini AI Summary Block ─────────────────────────────────────────────
const AiField = ({ label, children, mono = false }) => {
    if (!children || (Array.isArray(children) && children.length === 0)) return null;
    const text = Array.isArray(children) ? children.filter(Boolean).join(" · ") : children;
    if (!text) return null;
    return (
        <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-primary/70 mb-0.5 font-semibold">{label}</div>
            <div className={`text-xs text-foreground leading-relaxed ${mono ? "font-mono" : ""}`}>{text}</div>
        </div>
    );
};

function AiSummaryBlock({ summary, generatedAt, onRefresh, refreshBusy }) {
    const [showFullText, setShowFullText] = useState(false);
    if (!summary) return null;

    const g = summary.gpt_summary;  // GPT-4o-mini structured output

    // ── GPT summary path (primary) ────────────────────────────────────────────
    if (g) {
        const parties = g.parties ? `${g.parties.petitioner || "—"} vs ${g.parties.respondent || "—"}` : null;
        return (
            <div className="space-y-3" data-testid="gpt-summary-block">
                {/* Header badge */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5 font-semibold">
                            <Sparkles className="w-2.5 h-2.5"/> GPT-4o-mini · Enhanced Summary
                        </span>
                        {g.order_type && (
                            <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground border border-border rounded-full px-2 py-0.5">
                                {g.order_type}
                            </span>
                        )}
                    </div>
                    <button onClick={onRefresh} disabled={!onRefresh || refreshBusy}
                        data-testid="ai-summary-refresh"
                        className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground hover:text-primary disabled:opacity-40">
                        {refreshBusy ? "Refreshing…" : "Refresh"}
                    </button>
                </div>

                {/* Executive summary — full width */}
                {g.executive_summary && (
                    <div className="bg-primary/5 border border-primary/15 rounded-lg p-3" data-testid="gpt-executive-summary">
                        <div className="text-[9px] uppercase tracking-[0.22em] text-primary/70 mb-1 font-semibold">Summary</div>
                        <div className="text-xs text-foreground leading-relaxed">{g.executive_summary}</div>
                    </div>
                )}

                {/* 2-col grid for compact fields */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                    <AiField label="Outcome">{g.outcome}</AiField>
                    <AiField label="Court">{g.court}</AiField>
                    <AiField label="Judge">{g.judge}</AiField>
                    <AiField label="Next Date">{g.next_date}</AiField>
                    <AiField label="Parties">{parties}</AiField>
                </div>

                {/* Reasoning — full width */}
                {g.court_reasoning && (
                    <AiField label="Court Reasoning">{g.court_reasoning}</AiField>
                )}

                {/* Lists */}
                {Array.isArray(g.legal_issues) && g.legal_issues.length > 0 && (
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-primary/70 mb-1 font-semibold">Legal Issues</div>
                        <ul className="space-y-0.5">
                            {g.legal_issues.map((v, i) => (
                                <li key={i} className="text-xs text-foreground flex gap-1.5 leading-relaxed">
                                    <span className="text-primary mt-0.5 shrink-0">›</span>{v}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {Array.isArray(g.key_directions) && g.key_directions.length > 0 && (
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-primary/70 mb-1 font-semibold">Directions Given</div>
                        <ul className="space-y-0.5">
                            {g.key_directions.map((v, i) => (
                                <li key={i} className="text-xs text-foreground flex gap-1.5 leading-relaxed">
                                    <span className="text-amber-500 mt-0.5 shrink-0">›</span>{v}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {Array.isArray(g.action_items) && g.action_items.filter(Boolean).length > 0 && (
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-primary/70 mb-1 font-semibold">Action Items</div>
                        <ul className="space-y-0.5">
                            {g.action_items.filter(Boolean).map((v, i) => (
                                <li key={i} className="text-xs text-foreground flex gap-1.5 leading-relaxed">
                                    <span className="text-emerald-500 mt-0.5 shrink-0">›</span>{v}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {(Array.isArray(g.statutes_cited) && g.statutes_cited.length > 0) && (
                    <AiField label="Statutes Cited">{g.statutes_cited}</AiField>
                )}
                {(Array.isArray(g.case_laws_cited) && g.case_laws_cited.length > 0) && (
                    <AiField label="Case Laws Cited">{g.case_laws_cited}</AiField>
                )}

                {/* Full text toggle */}
                {summary.extracted_text && (
                    <div className="pt-2 border-t border-border">
                        <button onClick={() => setShowFullText(v => !v)}
                            data-testid="ai-extracted-text-toggle"
                            className="text-[10px] text-muted-foreground hover:text-primary uppercase tracking-[0.16em]">
                            {showFullText ? "Hide" : "Show"} full order text ({(summary.extracted_text.length / 1000).toFixed(1)}k chars)
                        </button>
                        {showFullText && (
                            <pre className="mt-2 max-h-96 overflow-auto text-[10px] text-muted-foreground bg-muted border border-border rounded-lg p-3 whitespace-pre-wrap font-mono">
                                {summary.extracted_text}
                            </pre>
                        )}
                    </div>
                )}

                {generatedAt && (
                    <div className="text-[9px] text-muted-foreground">Generated · {fmtDateTime(generatedAt)}</div>
                )}
            </div>
        );
    }

    // ── eCourts AI analysis fallback ──────────────────────────────────────────
    const ai = summary.ai_analysis || {};
    const insights = ai.intelligent_insights_analytics?.order_significance_and_impact_assessment || {};
    const meta = ai.foundational_metadata?.core_case_identifiers || {};
    const proc = ai.foundational_metadata?.procedural_details_from_order || {};
    const legal = ai.deep_legal_substance_context?.core_legal_content_analysis || {};
    const reasoning = ai.deep_legal_substance_context?.arguments_and_reasoning_analysis || {};
    const hasStructured = !!summary.ai_analysis;

    const _joinArr = (a) => Array.isArray(a) ? a.filter(Boolean).join(" · ") : a;
    const _fmtStatutes = (arr) => Array.isArray(arr)
        ? arr.map(s => `${s.act_name || ""} ${s.section_article_rule || ""}`.trim()).filter(Boolean).join(" · ")
        : null;

    const legacyKeys = ["executive_summary", "summary", "ratio_decidendi", "issues",
                        "statutes_cited", "case_laws_cited", "key_findings", "directions"];
    const legacyEntries = hasStructured ? [] : legacyKeys.filter(k => summary[k]).map(k => [k, summary[k]]);

    return (
        <div className="space-y-2" data-testid="ecourts-summary-block">
            <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground border border-border rounded-full px-2 py-0.5">
                    eCourts Analysis
                </span>
                <button onClick={onRefresh} disabled={!onRefresh || refreshBusy}
                    data-testid="ai-summary-refresh"
                    className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground hover:text-primary disabled:opacity-40">
                    {refreshBusy ? "Refreshing…" : "Refresh to get GPT summary"}
                </button>
            </div>
            {hasStructured ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    <AiField label="Executive Summary">{insights.ai_generated_executive_summary}</AiField>
                    <AiField label="Outcome">{insights.plain_language_summary_for_litigants_outcome_focused}</AiField>
                    <AiField label="Ratio Decidendi">{reasoning.ratio_decidendi_extracted?.statement}</AiField>
                    <AiField label="Court Reasoning">{reasoning.court_reasoning_for_decision}</AiField>
                    <AiField label="Issues">{_joinArr(legal.primary_legal_issues_identified)}</AiField>
                    <AiField label="Statutes Cited">{_fmtStatutes(legal.statutes_cited_and_applied)}</AiField>
                    <AiField label="Directions">{_joinArr(proc.specific_directions_given_by_court)}</AiField>
                    <AiField label="Disposition">{proc.disposition_outcome_if_disposed}</AiField>
                </div>
            ) : (
                <>
                    {legacyEntries.map(([k, v]) => (
                        <AiField key={k} label={k.replace(/_/g, " ")}>
                            {Array.isArray(v) ? v.join(" · ") : String(v)}
                        </AiField>
                    ))}
                    {legacyEntries.length === 0 && (
                        <div className="text-xs text-amber-600/80 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 space-y-2">
                            <div>Cached summary is from an older format. Refresh to get GPT-4o-mini enhanced summary.</div>
                            <button onClick={onRefresh} disabled={!onRefresh || refreshBusy}
                                data-testid="ai-summary-refresh"
                                className="px-3 py-1.5 border border-primary text-primary text-[10px] uppercase tracking-[0.16em] rounded-lg hover:bg-primary hover:text-primary-foreground disabled:opacity-40">
                                {refreshBusy ? "Refreshing…" : "Refresh AI summary"}
                            </button>
                        </div>
                    )}
                </>
            )}
            {summary.extracted_text && (
                <div className="pt-2 border-t border-border">
                    <button onClick={() => setShowFullText(v => !v)}
                        data-testid="ai-extracted-text-toggle"
                        className="text-[10px] text-muted-foreground hover:text-primary uppercase tracking-[0.16em]">
                        {showFullText ? "Hide" : "Show"} full order text ({(summary.extracted_text.length / 1000).toFixed(1)}k chars)
                    </button>
                    {showFullText && (
                        <pre className="mt-2 max-h-96 overflow-auto text-[10px] text-muted-foreground bg-muted border border-border rounded-lg p-3 whitespace-pre-wrap font-mono">
                            {summary.extracted_text}
                        </pre>
                    )}
                </div>
            )}
            {generatedAt && (
                <div className="text-[9px] text-muted-foreground pt-1">Generated · {fmtDateTime(generatedAt)}</div>
            )}
        </div>
    );
}


// ─── HOT/COLD watch-tier badge + toggle ──────────────────────────────────────
function WatchTierBadge({ caseId, tier, onChanged }) {
    // Default to showing the badge optimistically — hide only when API confirms S2 is off
    const [hotSlots, setHotSlots] = useState({ strategy_2_enabled: true, hot_used: 0, hot_max: 50 });
    const [loaded, setLoaded]     = useState(false);
    const [toggling, setToggling] = useState(false);
    const isHot = tier === "hot";

    useEffect(() => {
        api.get("/ecourts/strategy2/hot-slots")
            .then(r => { setHotSlots(r.data); setLoaded(true); })
            .catch(() => setLoaded(true)); // on failure keep default (show badge)
    }, [tier]);

    // Show badge when S2 OR S3 is active. In S3 it's a label (no slot cap).
    if (loaded && !hotSlots?.strategy_2_enabled && !hotSlots?.strategy_3_enabled) return null;

    const isS3 = !!hotSlots?.strategy_3_enabled;

    const toggle = async () => {
        setToggling(true);
        try {
            const { data } = await api.patch(`/ecourts/cases/${caseId}/watch-tier`);
            setHotSlots(prev => ({ ...prev, hot_used: data.hot_used }));
            onChanged?.();
            toast.success(data.new_tier === "hot"
                ? isS3
                    ? "Marked HOT — preference saved (all cases refresh equally in S3)"
                    : `Marked HOT — daily syncs enabled (${data.hot_used}/${data.hot_max} slots used)`
                : isS3
                    ? "Moved to COLD — preference saved"
                    : "Moved to COLD tier — weekly syncs");
        } catch (e) { toast.error(apiError(e)); }
        finally { setToggling(false); }
    };

    return (
        <div className="flex items-center gap-1.5" data-testid="watch-tier-badge">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Watch:</span>
            <button
                onClick={toggle}
                disabled={toggling}
                data-testid="watch-tier-toggle"
                title={isHot
                    ? isS3
                        ? "HOT preference (label only in S3 — all cases refresh equally)"
                        : `HOT tier — checked daily. Click to move to COLD. ${hotSlots?.hot_used ?? 0}/${hotSlots?.hot_max ?? 50} slots used.`
                    : isS3
                        ? "COLD preference (label only in S3 — all cases refresh equally)"
                        : `COLD tier — checked weekly. Click to mark HOT. ${hotSlots?.hot_used ?? 0}/${hotSlots?.hot_max ?? 50} slots used.`}
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide border transition-all disabled:opacity-50 cursor-pointer
                    ${isHot
                        ? "bg-orange-500/15 border-orange-400/50 text-orange-400 hover:bg-orange-500/25 hover:border-orange-400"
                        : "bg-sky-500/10 border-sky-400/40 text-sky-400 hover:bg-sky-500/20 hover:border-sky-400"}`}>
                {toggling
                    ? <Loader2 className="w-2.5 h-2.5 animate-spin"/>
                    : isHot
                        ? <Flame className="w-2.5 h-2.5"/>
                        : <span className="text-[9px] leading-none">❄</span>
                }
                {isHot ? "HOT" : "COLD"}
            </button>
            {loaded && hotSlots?.hot_max && (
                <span className="text-[10px] text-muted-foreground tabular-nums" data-testid="hot-slots-counter">
                    {hotSlots.hot_used}/{hotSlots.hot_max}
                </span>
            )}
        </div>
    );
}

// ─── "Possibly closed" banner ─────────────────────────────────────────────────
export function PossiblyClosedBanner({ caseId, onResolved }) {
    const [loading, setLoading] = useState(false);

    const resolve = async (action) => {
        setLoading(true);
        try {
            await api.patch(`/cases/${caseId}/possibly-closed`, { action });
            toast.success(action === "confirm" ? "Case marked as closed" : "Flag dismissed");
            onResolved?.();
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded px-4 py-3 flex items-start justify-between gap-4"
            data-testid="possibly-closed-banner">
            <div>
                <div className="text-xs font-semibold text-amber-300 mb-0.5">Possibly Closed</div>
                <div className="text-xs text-muted-foreground">
                    No activity detected for an extended period. Please confirm if this case is still active.
                </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => resolve("dismiss")} disabled={loading}
                    className="text-[11px] px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground disabled:opacity-50">
                    Still Active
                </button>
                <button onClick={() => resolve("confirm")} disabled={loading}
                    className="text-[11px] px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 disabled:opacity-50">
                    Confirm Closed
                </button>
            </div>
        </div>
    );
}


// ── Inline new-client mini-modal (opened from the case edit form) ─────────────
function NewClientMiniModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", mobile: "", email: "" });
    const [busy, setBusy] = useState(false);
    const upd = k => e => setForm({ ...form, [k]: e.target.value });

    const save = async (ev) => {
        ev.preventDefault();
        if (!form.name.trim()) { toast.error("Client name is required"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/clients", form);
            onCreated(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Modal open={true} onClose={onClose} title="New client" size="md" testId="case-edit-new-client-modal">
            <form onSubmit={save} className="space-y-4">
                <Field label="Full name">
                    <Input required autoFocus value={form.name} onChange={upd("name")} data-testid="case-newclient-name" placeholder="e.g. Ramesh Kumar"/>
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Mobile">
                        <Input value={form.mobile} onChange={upd("mobile")} data-testid="case-newclient-mobile" inputMode="tel" placeholder="+91 98765 43210"/>
                    </Field>
                    <Field label="Email">
                        <Input type="email" value={form.email} onChange={upd("email")} data-testid="case-newclient-email"/>
                    </Field>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
                    <GoldButton type="submit" disabled={busy} data-testid="case-newclient-save">
                        {busy ? "Saving…" : "Create & link client"}
                    </GoldButton>
                </div>
            </form>
        </Modal>
    );
}
